/**
 * @broadpaper/cloud-fonts — the typefaces BroadPaper Cloud documents are set in,
 * and the one trap Cloud is in a position to close.
 *
 * `CLAUDE.md` records the trap: the demo's themes name Inter, nothing embeds
 * Inter in the Forme measurer, so the engine measures with its standard faces,
 * and a section overlaps the tail of the one before it on the canvas while the
 * exported PDF is right. The stated fix is to embed the font the theme names
 * rather than trimming the copy until the two happen to agree — and Cloud is the
 * first host that owns both ends and can simply do that.
 *
 * So: a **closed** set of four families, the same bytes measuring the canvas and
 * drawing the file. Closed is the point. Every font a user could name is a font
 * we would have to embed on both sides or accept a canvas that lies, and a font
 * a user could upload is a licensing liability we would be hosting. Four families
 * cover a business document comfortably; a fifth is a decision, not a setting.
 *
 * All four are SIL OFL 1.1, whose terms are met by shipping the licence with the
 * font — `licences/` holds each family's own text, which is why they are files in
 * this package rather than a line in a table somewhere. Private and never
 * published: these bytes reach a browser from Cloud's own origin and reach a PDF
 * as an embedded subset, and neither is an npm install.
 */

export interface CloudFontFace {
  weight: 400 | 600 | 700;
  style: "normal" | "italic";
  /** File name inside `files/`, which is also the path the app serves it at. */
  file: string;
}

export interface CloudFontFamily {
  /** The `font-family` a theme names. Must match what the browser is given. */
  family: string;
  /** What a user picking a look sees. Never the family name on its own. */
  label: string;
  /** One line on when to reach for it. */
  note: string;
  kind: "serif" | "sans";
  /** Where it is meant to be used, which is what the theme picker groups by. */
  role: "display" | "body" | "both";
  faces: CloudFontFace[];
  /** The licence file in `licences/` whose terms this family is used under. */
  licence: string;
}

/**
 * The set.
 *
 * Fraunces and IBM Plex Sans are BroadPaper's own faces, so a document can look
 * like it came from here. Inter and Source Serif 4 are the neutral pair, so a
 * document can look like it came from the customer instead — which most of them
 * should.
 *
 * Four faces each and no more: regular, italic, semibold, bold. A document needs
 * a body, an emphasis, a heading and a total; nine weights of each family is
 * eight megabytes of download to give somebody a choice they will not make.
 */
export const CLOUD_FONTS: readonly CloudFontFamily[] = [
  {
    family: "Fraunces",
    label: "Fraunces",
    note: "A warm old-style serif with real personality at large sizes. BroadPaper's own display face.",
    kind: "serif",
    role: "display",
    licence: "fraunces-OFL.txt",
    faces: [
      { weight: 400, style: "normal", file: "Fraunces_400Regular.ttf" },
      { weight: 400, style: "italic", file: "Fraunces_400Regular_Italic.ttf" },
      { weight: 600, style: "normal", file: "Fraunces_600SemiBold.ttf" },
      { weight: 700, style: "normal", file: "Fraunces_700Bold.ttf" }
    ]
  },
  {
    family: "IBM Plex Sans",
    label: "IBM Plex Sans",
    note: "A typewriter ancestry that suits a document about paper. Reads well at small sizes in a table.",
    kind: "sans",
    role: "both",
    licence: "ibm-plex-sans-OFL.txt",
    faces: [
      { weight: 400, style: "normal", file: "IBMPlexSans_400Regular.ttf" },
      { weight: 400, style: "italic", file: "IBMPlexSans_400Regular_Italic.ttf" },
      { weight: 600, style: "normal", file: "IBMPlexSans_600SemiBold.ttf" },
      { weight: 700, style: "normal", file: "IBMPlexSans_700Bold.ttf" }
    ]
  },
  {
    family: "Inter",
    label: "Inter",
    note: "Neutral, modern and unmistakably a business document. The safe choice for anything sent to a customer.",
    kind: "sans",
    role: "both",
    licence: "inter-OFL.txt",
    faces: [
      { weight: 400, style: "normal", file: "Inter_400Regular.ttf" },
      { weight: 400, style: "italic", file: "Inter_400Regular_Italic.ttf" },
      { weight: 600, style: "normal", file: "Inter_600SemiBold.ttf" },
      { weight: 700, style: "normal", file: "Inter_700Bold.ttf" }
    ]
  },
  {
    family: "Source Serif 4",
    label: "Source Serif",
    note: "A serif built for long passages on paper. Use it where a document has something to read rather than something to scan.",
    kind: "serif",
    role: "both",
    licence: "source-serif-4-OFL.txt",
    faces: [
      { weight: 400, style: "normal", file: "SourceSerif4_400Regular.ttf" },
      { weight: 400, style: "italic", file: "SourceSerif4_400Regular_Italic.ttf" },
      { weight: 600, style: "normal", file: "SourceSerif4_600SemiBold.ttf" },
      { weight: 700, style: "normal", file: "SourceSerif4_700Bold.ttf" }
    ]
  }
];

/** Every face, flat, with the family each belongs to. */
export const CLOUD_FONT_FACES: ReadonlyArray<CloudFontFace & { family: string }> = CLOUD_FONTS.flatMap((f) => f.faces.map((face) => ({ ...face, family: f.family })));

/** The families a theme may name. Anything else measures with the engine's standard faces and lies. */
export const CLOUD_FONT_FAMILIES: readonly string[] = CLOUD_FONTS.map((f) => f.family);

/**
 * Whether a theme's font is one we can honour.
 *
 * Used to refuse a template whose theme names something else, rather than
 * rendering it with a substituted face and letting the canvas and the file
 * disagree by a line per paragraph. A refusal is recoverable; a document that is
 * subtly wrong is not.
 */
export function isCloudFont(family: string | undefined): boolean {
  if (!family) return false;
  // A CSS stack is `"Inter", system-ui, sans-serif`; the first name is the one
  // that matters and the rest are the browser's fallbacks.
  const first = family.split(",")[0]!.trim().replace(/^["']|["']$/g, "");
  return CLOUD_FONT_FAMILIES.some((f) => f.toLowerCase() === first.toLowerCase());
}

/**
 * The `@font-face` rules the browser needs, given where the files are served.
 *
 * `font-display: block` rather than `swap`, deliberately and against the usual
 * advice: the canvas measures text as soon as it can, and a fallback face
 * measured and then replaced means the page breaks visibly move under the user.
 * A short invisible pause is better than a document that rearranges itself.
 */
export function fontFaceCss(base: string): string {
  const root = base.replace(/\/$/, "");
  return CLOUD_FONT_FACES.map(
    (face) =>
      `@font-face{font-family:"${face.family}";src:url("${root}/${face.file}") format("truetype");font-weight:${face.weight};font-style:${face.style};font-display:block;}`
  ).join("");
}
